// src/screens/InvitedUserDetails.js (or wherever this file lives)
// ✅ DROP-IN REPLACEMENT
// - Customers AND staff must verify email (password auth)
// - Sends verification email automatically on Finish Setup (new + recovered accounts)
// - Routes everyone to /verify-email (your App.js gate already blocks unverified users)
// - Fixes /login?verified=true -> /login?verified=1
// - Uses the SAME continue URL style as App.js/Login.js (handleCodeInApp: true)

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  onAuthStateChanged,
  signOut,
  sendEmailVerification,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { getEmailVerificationActionCodeSettings } from "../auth/authState";
import { setStoredActiveCustomerCompany, upsertCustomerMembership } from "../auth/customerMemberships";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

// Throttle verification sends to avoid duplicates when navigating fast
const verifySentKey = (uid) => `verifyEmailSentAt_${uid}`;

const markVerifySent = (uid) => {
  try {
    window.localStorage.setItem(verifySentKey(uid), String(Date.now()));
  } catch (_) {}
};

const recentlySentVerify = (uid, windowMs = 60_000) => {
  try {
    const raw = window.localStorage.getItem(verifySentKey(uid));
    const last = raw ? Number(raw) : 0;
    return last && Date.now() - last < windowMs;
  } catch (_) {
    return false;
  }
};

const InvitedUserDetails = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const inviteId = query.get("inviteId");

  const [invite, setInvite] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [existingAccount, setExistingAccount] = useState(false);
  const [checkingExistingAccount, setCheckingExistingAccount] = useState(false);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Verification panel states
  const [phase, setPhase] = useState("form"); // "form" | "verify"
  const [sendingVerify, setSendingVerify] = useState(false);
  const [checkingVerify, setCheckingVerify] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState("");
  const [autoAcceptingExistingInvite, setAutoAcceptingExistingInvite] = useState(false);

  const [error, setError] = useState("");

  const [form, setForm] = useState({
    fullName: "",
    password: "",
    confirmPassword: "",
    // address
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    // end user fields
    jobTitle: "",
    territory: "",
    // customer fields
    interests: "",
    customInterest: "",
    notes: "",
  });

  const roleLower = useMemo(() => {
    return (invite?.role || "").toString().trim().toLowerCase();
  }, [invite?.role]);

  const isCustomer = roleLower === "customer";

  // Track current auth user
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setAuthUser(u || null);
    });
    return () => unsub();
  }, []);

  // Load invite
  useEffect(() => {
    const loadInvite = async () => {
      if (!inviteId) {
        setError("Missing invite ID in the link.");
        setLoading(false);
        return;
      }

      try {
        const inviteRef = doc(db, "invites", inviteId);
        const snap = await getDoc(inviteRef);

        if (!snap.exists()) {
          setError("This invitation is invalid or has expired.");
          setLoading(false);
          return;
        }

        const data = snap.data();
        const status = (data.status || "Pending").toString();

        // If invite is not pending, only allow if current auth user matches invite email
        const currentEmail = (auth.currentUser?.email || "").toLowerCase();
        const inviteEmail = (data.email || "").toLowerCase();

        if (status !== "Pending" && (!currentEmail || currentEmail !== inviteEmail)) {
          setError("This invitation has already been used. Please log in with the invited email.");
          setLoading(false);
          return;
        }

        setInvite({ id: inviteId, ...data });

        // Prefill name from invite
        setForm((prev) => ({
          ...prev,
          fullName: data.fullName || "",
        }));
      } catch (err) {
        console.error("Error loading invite:", err);

        if (err.code === "permission-denied") {
          setError("Unable to access this invitation. The link may be invalid or expired.");
        } else if (err.code === "unavailable") {
          setError("Network error. Please check your internet connection and try again.");
        } else {
          setError("Failed to load invitation. Please try again or contact support.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteId]);

  // ✅ If user is signed in for this invite and not verified, show verify panel for ALL roles
  useEffect(() => {
    if (!invite || !authUser) return;

    const inviteEmail = (invite.email || "").toLowerCase();
    const currentEmail = (authUser.email || "").toLowerCase();

    if (currentEmail === inviteEmail && !authUser.emailVerified) {
      setPhase("verify");
    }
  }, [invite, authUser]);

  useEffect(() => {
    const invitedEmail = (invite?.email || "").trim();
    const inviteSaysExisting = Boolean(invite?.existingSupplyHawkUser || invite?.existingUserUid);
    if (!invitedEmail) {
      setExistingAccount(false);
      setCheckingExistingAccount(false);
      return;
    }

    if (inviteSaysExisting) {
      setExistingAccount(true);
      setCheckingExistingAccount(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setCheckingExistingAccount(true);
      try {
        const methods = await fetchSignInMethodsForEmail(auth, invitedEmail);
        if (!cancelled) setExistingAccount(Array.isArray(methods) && methods.length > 0);
      } catch (err) {
        console.warn("[InvitedUserDetails] Could not check existing sign-in methods:", err);
        if (!cancelled) setExistingAccount(false);
      } finally {
        if (!cancelled) setCheckingExistingAccount(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [invite?.email, invite?.existingSupplyHawkUser, invite?.existingUserUid]);

  useEffect(() => {
    if (!invite || !authUser || !existingAccount || autoAcceptingExistingInvite || submitting) return;

    const invitedEmail = (invite?.email || "").toLowerCase().trim();
    const currentEmail = (authUser?.email || "").toLowerCase().trim();
    if (!invitedEmail || invitedEmail !== currentEmail) return;

    let cancelled = false;

    const run = async () => {
      setAutoAcceptingExistingInvite(true);
      setError("");
      try {
        const existingUserDoc = await getDoc(doc(db, "users", authUser.uid));
        if (!existingUserDoc.exists()) return;
        if (!cancelled) {
          await completeExistingInviteAcceptance({ user: authUser, existingUserDoc });
        }
      } catch (err) {
        console.error("[InvitedUserDetails] Auto-accept existing invite failed:", err);
        if (!cancelled) {
          setError(err?.message || "We couldn't connect this company to your existing login.");
        }
      } finally {
        if (!cancelled) setAutoAcceptingExistingInvite(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite, authUser, existingAccount, autoAcceptingExistingInvite, submitting]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Send verification email using same settings as App.js/Login.js
  const sendVerificationNow = async (user) => {
    if (!user) return;

    try {
      await user.reload();
    } catch (_) {}

    if (user.emailVerified) return;

    if (recentlySentVerify(user.uid)) return;

    await sendEmailVerification(user, getEmailVerificationActionCodeSettings());

    markVerifySent(user.uid);
    setVerifyMessage("Verification email sent. Check your inbox (and spam).");
  };

  const handleSendVerification = async () => {
    setError("");
    setVerifyMessage("");

    if (!auth.currentUser) {
      setError("No authenticated user found. Please log in and try again.");
      return;
    }

    setSendingVerify(true);
    try {
      await sendVerificationNow(auth.currentUser);
    } catch (err) {
      console.error("[InvitedUserDetails] sendEmailVerification failed:", err);
      setError(err?.message || "Failed to send verification email.");
    } finally {
      setSendingVerify(false);
    }
  };

  const handleCheckVerified = async () => {
    setError("");
    setVerifyMessage("");

    if (!auth.currentUser) {
      setError("No authenticated user found. Please log in and try again.");
      return;
    }

    setCheckingVerify(true);
    try {
      await auth.currentUser.reload();

      if (!auth.currentUser.emailVerified) {
        setError("Still not verified. Click the link in your email, then try again.");
        return;
      }

      // Stamp Firestore (optional - your app gates off Firebase Auth anyway)
      try {
        await updateDoc(doc(db, "users", auth.currentUser.uid), {
          emailVerified: true,
          emailVerifiedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (stampErr) {
        console.warn("[InvitedUserDetails] Failed to stamp emailVerified in Firestore:", stampErr);
      }

      setVerifyMessage("Email verified! You'll be logged in automatically...");

      // Force fresh auth state via login page handler
      await signOut(auth);

      setTimeout(() => {
        navigate("/login?verified=1", { replace: true });
      }, 900);
    } catch (err) {
      console.error("[InvitedUserDetails] check verified failed:", err);
      setError(err?.message || "Could not check verification status.");
    } finally {
      setCheckingVerify(false);
    }
  };

  async function completeExistingInviteAcceptance({ user, existingUserDoc }) {
    if (!invite || !user || !existingUserDoc?.exists()) {
      throw new Error("We couldn't find the existing invited account.");
    }

    const { email, role, companyId, invitedByUserId } = invite;
    const resolvedCompanyId = companyId || null;
    const roleLowerLocal = (role || "endUser").toString().trim().toLowerCase();
    const isCustomerLocal = roleLowerLocal === "customer";

    if (isCustomerLocal && resolvedCompanyId) {
      await upsertCustomerMembership({
        db,
        uid: user.uid,
        companyId: resolvedCompanyId,
        companyName: invite.companyName || "",
        accountId: invite.accountId || "",
        accountName: invite.accountName || "",
        repUserId: invitedByUserId || null,
        role: "customer",
        isDefault: false,
        metadata: {
          inviteId: invite.id,
          invitedByUserId: invitedByUserId || null,
          fullName: existingUserDoc.data()?.fullName || form.fullName.trim(),
          email,
        },
      });

      if (invite.accountId) {
        await updateDoc(doc(db, "companies", resolvedCompanyId, "accounts", invite.accountId), {
          portalUserId: user.uid,
          primaryCustomerUserId: user.uid,
          portalUserIds: arrayUnion(user.uid),
          updatedAt: serverTimestamp(),
        });
      }
      setStoredActiveCustomerCompany({ uid: user.uid, companyId: resolvedCompanyId });
    }

    await updateDoc(doc(db, "invites", invite.id), {
      status: "Accepted",
      acceptedAt: serverTimestamp(),
      acceptedByUid: user.uid,
    });

    try {
      await sendVerificationNow(user);
    } catch (verifyErr) {
      console.error("[InvitedUserDetails] sendEmailVerification failed (existing user):", verifyErr);
    }

    if (user.emailVerified) {
      if (isCustomerLocal) {
        navigate("/customer?view=company", { replace: true });
      } else {
        navigate("/login?verified=1", { replace: true });
      }
      return;
    }

    setPhase("verify");
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setVerifyMessage("");

    if (!invite) {
      setError("Invite not loaded.");
      return;
    }

    const existingInviteForExistingAccount = existingAccount;

    if (!existingInviteForExistingAccount && !form.fullName.trim()) {
      setError("Please enter your name.");
      return;
    }

    if (!form.password || form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (!existingInviteForExistingAccount && form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);

    try {
      const { email, role, companyId, invitedByUserId } = invite;
      const resolvedCompanyId = companyId || null;
      const roleLowerLocal = (role || "endUser").toString().trim().toLowerCase();
      const isCustomerLocal = roleLowerLocal === "customer";
      const isStaffLocal = !isCustomerLocal;

      if (existingInviteForExistingAccount) {
        if (auth.currentUser && auth.currentUser.email !== email) {
          await signOut(auth);
        }

        const cred = await signInWithEmailAndPassword(auth, email, form.password);
        const existingUserDoc = await getDoc(doc(db, "users", cred.user.uid));

        if (!existingUserDoc.exists()) {
          throw new Error("We found your sign-in, but not your user profile. Please contact support.");
        }

        await completeExistingInviteAcceptance({ user: cred.user, existingUserDoc });
        setSubmitting(false);
        return;
      }

      // If someone else is logged in (e.g. owner/admin), sign them out first
      if (auth.currentUser && auth.currentUser.email !== email) {
        console.log("[InvitedUserDetails] Different user logged in → signing out first.");
        await signOut(auth);
      }

      let uid;
      let cred;
      // 1) Create Auth user OR sign in if already exists
      try {
        console.log("[InvitedUserDetails] Creating auth user for invite email:", email);
        cred = await createUserWithEmailAndPassword(auth, email, form.password);
        uid = cred.user.uid;
      } catch (authError) {
        if (authError.code === "auth/email-already-in-use") {
          console.log("[InvitedUserDetails] Email already exists in auth, attempting to sign in...");
          try {
            cred = await signInWithEmailAndPassword(auth, email, form.password);
            uid = cred.user.uid;
            const existingUserDoc = await getDoc(doc(db, "users", uid));
            if (existingUserDoc.exists()) {
              console.log("[InvitedUserDetails] User doc exists, updating invite and proceeding...");
              await completeExistingInviteAcceptance({ user: cred.user, existingUserDoc });
              setSubmitting(false);
              return;
            }

            console.log("[InvitedUserDetails] Recovering orphaned auth account - will create user doc with uid:", uid);
          } catch (signInError) {
            console.error("[InvitedUserDetails] Sign in attempt failed:", signInError);
            if (signInError.code === "auth/wrong-password") {
              setError(
                "This email is already registered with a different password. Please contact support or use 'Forgot Password'."
              );
            } else if (signInError.code === "auth/too-many-requests") {
              setError("Too many failed attempts. Please try again later or reset your password.");
            } else {
              setError("This email is already registered. Please use the login page or contact support.");
            }
            setSubmitting(false);
            return;
          }
        } else {
          throw authError;
        }
      }

      // 1b) ✅ ALWAYS try to send verification email for password-auth users (customer + staff + recovered)
      try {
        await sendVerificationNow(cred.user);
      } catch (verifyErr) {
        console.error("[InvitedUserDetails] sendEmailVerification failed:", verifyErr);
        setError(
          verifyErr?.message ||
            "We couldn't send the verification email. Please click 'Resend verification email'."
        );
        // Don't abort; user can still proceed to /verify-email and resend
      }

      // 2) Customer must have accountId
      const accountId = isCustomerLocal ? (invite.accountId || null) : null;
      if (isCustomerLocal && !accountId) {
        throw new Error("This customer invite is missing an account link. Ask your rep to resend the invite.");
      }

      // 3) Staff group assignment
      const inviteStaffGroupId = invite?.staffGroupId || null;
      const initialGroupIds = isStaffLocal && inviteStaffGroupId ? [inviteStaffGroupId] : [];

      // 4) Create user profile doc
      const baseUser = {
        uid,
        email,
        fullName: form.fullName.trim(),
        role: role || "endUser",
        companyId: resolvedCompanyId,
        status: "Active",
        fromInvite: true,
        inviteId: invite.id,
        setupCompleted: true,
        accountId: accountId || null,
        repUserId: invitedByUserId || null,

        // ✅ Do NOT force customers to "true" anymore. Use Firebase Auth truth.
        emailVerified: cred?.user?.emailVerified === true,

        groupIds: initialGroupIds,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const address = {
        addressLine1: form.addressLine1 || "",
        addressLine2: form.addressLine2 || "",
        city: form.city || "",
        state: form.state || "",
        zip: form.zip || "",
      };

      const extra = isCustomerLocal
        ? {
            customerNotes: form.notes || "",
            interests: form.interests === "custom" ? form.customInterest || "" : form.interests || "",
          }
        : {
            jobTitle: form.jobTitle || "",
            territory: form.territory || "",
          };

      const userDoc = { ...baseUser, address, ...extra };

      console.log("[InvitedUserDetails] Writing /users doc:", uid);
      await setDoc(doc(db, "users", uid), userDoc);

      if (isCustomerLocal && resolvedCompanyId) {
        await upsertCustomerMembership({
          db,
          uid,
          companyId: resolvedCompanyId,
          companyName: invite.companyName || "",
          accountId: accountId || "",
          accountName: invite.accountName || "",
          repUserId: invitedByUserId || null,
          role: "customer",
          isDefault: true,
          metadata: {
            inviteId: invite.id,
            invitedByUserId: invitedByUserId || null,
            fullName: form.fullName.trim(),
            email,
          },
        });
        if (accountId) {
          await updateDoc(doc(db, "companies", resolvedCompanyId, "accounts", accountId), {
            portalUserId: uid,
            primaryCustomerUserId: uid,
            portalUserIds: arrayUnion(uid),
            updatedAt: serverTimestamp(),
          });
        }
        setStoredActiveCustomerCompany({ uid, companyId: resolvedCompanyId });
      }

      const check = await getDoc(doc(db, "users", uid));
      console.log("[InvitedUserDetails] /users write check exists?", check.exists());
      if (!check.exists()) {
        throw new Error(
          "User profile was not created in Firestore (/users). This is usually a Firestore rules issue."
        );
      }

      // 4b) If staffGroupId wasn't on the invite, patch groupIds ONCE
      if (isStaffLocal && !inviteStaffGroupId && resolvedCompanyId) {
        try {
          console.log("[InvitedUserDetails] Attempting one-time staff groupIds patch...");
          const companySnap = await getDoc(doc(db, "companies", resolvedCompanyId));
          const staffGroupId = companySnap.exists() ? companySnap.data()?.staffGroupId || null : null;

          if (staffGroupId) {
            await updateDoc(doc(db, "users", uid), {
              groupIds: arrayUnion(staffGroupId),
              updatedAt: serverTimestamp(),
            });
            console.log("[InvitedUserDetails] Patched groupIds with:", staffGroupId);
          } else {
            console.warn(
              "[InvitedUserDetails] company.staffGroupId missing; staff user may not see accounts until set."
            );
          }
        } catch (patchErr) {
          console.warn("[InvitedUserDetails] groupIds patch failed:", patchErr);
        }
      }

      // 5) Mark invite accepted
      console.log("[InvitedUserDetails] Marking invite accepted:", invite.id);
      await updateDoc(doc(db, "invites", invite.id), {
        status: "Accepted",
        acceptedAt: serverTimestamp(),
        acceptedByUid: uid,
      });

      // 6) ✅ Customers verify too — always go to /verify-email
      setPhase("verify");
    } catch (err) {
      console.error("Error completing invite:", err);
      setError(
        err?.message ||
          "Failed to complete your account. Please double-check your info and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- UI STATES ----------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600 text-lg">Loading your invitation…</p>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-md p-6 max-w-md w-full">
          <p className="text-red-600 mb-3 font-semibold">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="mt-2 inline-flex items-center justify-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------
  // VERIFY PHASE (ALL roles now)
  // ---------------------------
  if (phase === "verify") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md p-6 max-w-lg w-full border border-gray-200">
          <h1 className="text-xl font-bold text-gray-900 mb-1">Verify your email</h1>
          <p className="text-sm text-gray-600 mb-4">
            We sent a verification link to <strong>{invite?.email}</strong>. Please verify your email to continue.
          </p>

          {verifyMessage && (
            <div className="mb-3 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              {verifyMessage}
            </div>
          )}

          {error && (
            <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <button
              type="button"
              onClick={handleSendVerification}
              disabled={sendingVerify}
              className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
            >
              {sendingVerify ? "Sending…" : "Resend verification email"}
            </button>

            <button
              type="button"
              onClick={handleCheckVerified}
              disabled={checkingVerify}
              className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-800 text-sm font-semibold hover:bg-gray-50 disabled:opacity-60"
            >
              {checkingVerify ? "Checking…" : "I verified — continue"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full text-xs text-gray-500 hover:text-gray-700 mt-2"
            >
              Go to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------
  // DEFAULT FORM PHASE
  // ---------------------------
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-md p-6 max-w-lg w-full">
        <h1 className="text-xl font-bold text-gray-900 mb-1">
          Welcome to {invite?.companyName || "SupplyHawk"}
        </h1>
        <p className="text-sm text-gray-500 mb-4">
          {existingAccount
            ? "This email already has a SupplyHawk login. Sign in to connect this company."
            : "Complete your details to finish setting up your account."}
        </p>

        {authUser && authUser.email !== invite?.email && (
          <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-800">
            You are currently logged in as <strong>{authUser.email}</strong>.
            This invite is for <strong>{invite?.email}</strong>. We&apos;ll log
            you out before continuing so the existing customer account can be
            connected correctly.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          {!existingAccount ? (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              required
            />
          </div>
          ) : null}

          {/* Email (read-only) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={invite?.email || ""}
              disabled
              className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-600"
            />
            <p className="mt-1 text-[11px] text-gray-400">
              This is the email your invitation was sent to.
            </p>
          </div>

          {/* Password + confirm */}
          <div className={`grid gap-3 ${existingAccount ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {existingAccount ? "Password" : "Create password"}
              </label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                required
              />
            </div>
            {!existingAccount ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Confirm password</label>
              <input
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                required
              />
            </div>
            ) : null}
          </div>

          {/* Mailing address */}
          {!existingAccount ? (
          <div className="border-t border-gray-100 pt-3 mt-1">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">Mailing address</h2>
            {isCustomer && (
              <p className="text-[11px] text-gray-500 mb-2">
                This is where samples will be sent. You can change it later when placing an order.
              </p>
            )}
            <div className="space-y-2">
              <input
                type="text"
                name="addressLine1"
                value={form.addressLine1}
                onChange={handleChange}
                placeholder="Street address"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                required
              />
              <input
                type="text"
                name="addressLine2"
                value={form.addressLine2}
                onChange={handleChange}
                placeholder="Apt, suite, etc. (optional)"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="City"
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                />
                <input
                  type="text"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  placeholder="State"
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                />
                <input
                  type="text"
                  name="zip"
                  value={form.zip}
                  onChange={handleChange}
                  placeholder="ZIP"
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                />
              </div>
              </div>
            </div>
          ) : null}

          {/* Role-specific section */}
          {!existingAccount && !isCustomer ? (
            <div className="border-t border-gray-100 pt-3 mt-1 space-y-2">
              <h2 className="text-sm font-semibold text-gray-800 mb-1">Work details</h2>
              <input
                type="text"
                name="jobTitle"
                value={form.jobTitle}
                onChange={handleChange}
                placeholder="Job title"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <input
                type="text"
                name="territory"
                value={form.territory}
                onChange={handleChange}
                placeholder="Territory (optional)"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          ) : !existingAccount ? (
            <div className="border-t border-gray-100 pt-3 mt-1 space-y-2">
              <h2 className="text-sm font-semibold text-gray-800 mb-1">Interests (optional)</h2>
              <select
                name="interests"
                value={form.interests}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select a category</option>
                <option value="Seafood">Seafood</option>
                <option value="Meat">Meat</option>
                <option value="Shellfish">Shellfish</option>
                <option value="Produce">Produce</option>
                <option value="Bakery">Bakery</option>
                <option value="Dairy">Dairy</option>
                <option value="custom">Other (type your own)</option>
              </select>
              {form.interests === "custom" && (
                <input
                  type="text"
                  name="customInterest"
                  value={form.customInterest}
                  onChange={handleChange}
                  placeholder="Describe what you're interested in"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              )}
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Notes for your rep (optional)"
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          ) : null}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || autoAcceptingExistingInvite || checkingExistingAccount}
            className="w-full mt-2 inline-flex items-center justify-center px-4 py-2.5 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {autoAcceptingExistingInvite
              ? "Connecting…"
              : submitting
              ? "Saving…"
              : existingAccount
              ? "Sign in and accept invite"
              : "Finish setup"}
          </button>

          <p className="text-[11px] text-gray-400 mt-2">
            {existingAccount
              ? "Use your existing password for this email to connect the new company invitation."
              : "After setup, you must verify your email before accessing the app."}
          </p>
        </form>
      </div>
    </div>
  );
};

export default InvitedUserDetails;
